import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import type { Chart } from 'highcharts';
import { computed, toRef } from 'vue';
import { RotateCcw } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import { Button } from '@/components/ui/button';
import { i18n } from '@/plugins/internationalization/i18n';
import type { RecordWeekBin } from '@/composables/records/useRecordsAggregation';
import { clearHoverBand, useRecordsChartOptions } from '@/composables/records/useRecordsChartOptions';

/*
 * The weekly column chart the records pages navigate their table with: one bar per week, click to
 * select, click again to clear. A title and a colour are all that differ between those pages, so a
 * new page adds a heading and a colour token rather than another copy of this.
 *
 * The tooltip names what a bar counts, not which registration type produced it - the page heading
 * above already says that - so the wording is the same on every records page and is resolved here.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'RecordsWeeklyChart',
  props: {
    bins: {},
    selectedWeekStart: {},
    color: {},
    title: {},
    seriesName: {},
    pointDetail: { type: Function }
  },
  emits: ["week-selected", "selection-reset"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// Computed rather than a constant so the tooltip follows a language switch.
const seriesName = computed(() => props.seriesName ?? i18n.global.t('views.pigs.records.chart.seriesName'));

const emit = __emit;

const options = useRecordsChartOptions(toRef(props, 'bins'), toRef(props, 'selectedWeekStart'), {
    color: toRef(props, 'color'),
    title: toRef(props, 'title'),
    seriesName,
    pointDetail: (weekStart: string) => props.pointDetail?.(weekStart) ?? null,
    onBinClicked: (weekStart: string) => emit('week-selected', weekStart),
});

function onChartUpdate(instance: Chart) {
    // A redraw drops imperatively added bands; the next pointer move puts a fresh one back.
    clearHoverBand(instance);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GenericChart, {
    options: (_unref(options) as any),
    onUpdate: onChartUpdate
  }, {
    "prepend-actions": _withCtx(() => [
      _renderSlot(_ctx.$slots, "prepend-actions"),
      _createVNode(_unref(Button), {
        variant: "outline",
        size: "sm",
        disabled: _ctx.selectedWeekStart === null,
        "aria-label": _ctx.$t('views.pigs.records.chart.resetSelectionHint'),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('selection-reset')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(RotateCcw), {
            class: "size-4 mr-1.5",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('views.pigs.records.chart.resetSelection')), 1)
        ]),
        _: 1
      }, 8, ["disabled", "aria-label"])
    ]),
    _: 3
  }, 8, ["options"]))
}
}

})